import { schema } from "../../lib/supabase";
import type { JsonRecord, WorkContext } from "../../types";
import type { AceroRefuerzoDetalle, AceroRefuerzoPayload, CatalogosAceroRefuerzo } from "./steelTypes";

export interface QualityResultFilters {
  start?: string;
  end?: string;
  frontId?: string;
  subfrontId?: string;
  search?: string;
  dictamen?: string;
  page?: number;
  pageSize?: number;
}

export interface QualityResultList {
  rows: JsonRecord[];
  count: number;
  dictamenes: string[];
}

function unwrap(data: unknown, error: { message: string } | null): JsonRecord {
  if (error) throw new Error(error.message);
  return (data || {}) as JsonRecord;
}

async function callResultsApi(operation: "listar" | "actualizar" | "eliminar", resultType: string, context: WorkContext, payload: JsonRecord = {}) {
  const { data, error } = await schema("calidad").rpc("api_resultados_calidad" as never, {
    p_operacion: operation,
    p_tipo: resultType,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error);
}

async function callSteelApi(operation: "catalogos" | "listar" | "detalle" | "insertar" | "editar" | "eliminar", context: WorkContext, payload: JsonRecord = {}) {
  const { data, error } = await schema("calidad").rpc("api_certificados_acero_refuerzo" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  if (error) throw new Error(error.message);
  return data as unknown;
}

async function callCompactionApi(operation: "catalogos" | "insertar" | "editar_compactacion" | "editar_sondeo", context: WorkContext, payload: JsonRecord = {}) {
  const { data, error } = await schema("calidad").rpc("api_compactacion_suelos" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error);
}

export async function compactionResultCatalogs(context: WorkContext) {
  return callCompactionApi("catalogos", context);
}

export async function createCompactionResult(context: WorkContext, values: JsonRecord) {
  return callCompactionApi("insertar", context, { datos: values });
}

export interface CompactacionEditPayload {
  id: string;
  fecha_informe: string | null;
  mvsm: number | null;
  especificacion: number | null;
  tolerancia: number | null;
  comentarios: string | null;
}

export async function updateCompactionResult(context: WorkContext, values: CompactacionEditPayload) {
  return callCompactionApi("editar_compactacion", context, {
    id: values.id,
    fecha_informe: values.fecha_informe,
    mvsm: values.mvsm,
    especificacion: values.especificacion,
    tolerancia: values.tolerancia,
    comentarios: values.comentarios,
  });
}

export interface CompactacionSondeoUpdate {
  id: string;
  informe_id: string;
  x_sondeo: number | null;
  y_sondeo: number | null;
  masa_lugar: number | null;
  grado_reportado: number | null;
  comentarios: string | null;
}

export async function updateCompactionSounding(context: WorkContext, values: CompactacionSondeoUpdate) {
  return callCompactionApi("editar_sondeo", context, {
    id: values.id,
    informe_id: values.informe_id,
    x_sondeo: values.x_sondeo,
    y_sondeo: values.y_sondeo,
    masa_lugar: values.masa_lugar,
    grado_reportado: values.grado_reportado,
    comentarios: values.comentarios,
  });
}

function steelListResponse(value: unknown): QualityResultList {
  if (Array.isArray(value)) return { rows: value as JsonRecord[], count: value.length, dictamenes: [] };
  const response = (value || {}) as JsonRecord;
  const rows = (Array.isArray(response.rows) ? response.rows : Array.isArray(response.registros) ? response.registros : Array.isArray(response.data) ? response.data : []) as JsonRecord[];
  return {
    rows,
    count: Number(response.count ?? rows.length),
    dictamenes: Array.isArray(response.dictamenes) ? response.dictamenes.map(String) : [],
  };
}

export async function steelResultDetail(context: WorkContext, id: string) {
  const response = await callSteelApi("detalle", context, { id });
  if (Array.isArray(response)) return (response[0] || {}) as unknown as AceroRefuerzoDetalle;
  const wrapped = (response || {}) as JsonRecord;
  return ((wrapped.registro || wrapped.row || wrapped.data || response) || {}) as unknown as AceroRefuerzoDetalle;
}

export async function steelCatalogs(context: WorkContext) {
  return await callSteelApi("catalogos", context) as CatalogosAceroRefuerzo;
}

export async function createSteelResult(context: WorkContext, values: AceroRefuerzoPayload) {
  return callSteelApi("insertar", context, { datos: values });
}

export async function updateSteelResult(context: WorkContext, id: string, values: AceroRefuerzoPayload) {
  return callSteelApi("editar", context, { id, datos: values });
}

export async function deleteSteelResult(context: WorkContext, id: string) {
  return callSteelApi("eliminar", context, { id });
}

function compactLocation(row: JsonRecord) {
  const parts: string[] = [];
  const franja = String(row.franja || "").trim();
  if (franja) parts.push(`Franja: ${franja}`);
  const cadInicial = String(row.cad_inicial ?? "").trim();
  const cadFinal = String(row.cad_final ?? "").trim();
  const puntoInicial = String(row.punto_inicial || "").trim();
  const puntoFinal = String(row.punto_final || "").trim();
  const fallback = String(row.ubicacion || row.ubicacion_puntual || "").trim();
  if (cadInicial && cadFinal) parts.push(`${cadInicial} al ${cadFinal}`);
  else if (puntoInicial && puntoFinal) parts.push(`de ${puntoInicial} a ${puntoFinal}`);
  else if (fallback) parts.push(fallback);
  return parts.join(" · ") || null;
}

async function enrichCompactionSoundings(rows: JsonRecord[], context: WorkContext) {
  const ids = [...new Set(rows.map((row) => String(row.id || "")).filter(Boolean))];
  if (!ids.length) return rows.map((row) => ({ ...row, ubicacion_detalles: compactLocation(row), sondeos: [] }));

  const { data, error } = await schema("calidad").rpc("api_sondeos_compactacion_resultados" as never, {
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_informe_ids: ids,
  } as never);
  if (error) throw new Error(error.message);

  const soundings = Array.isArray(data) ? data as unknown as JsonRecord[] : [];
  const grouped = new Map<string, JsonRecord[]>();
  soundings.forEach((item) => {
    const key = String(item.informe_id || "");
    const list = grouped.get(key) || [];
    list.push(item);
    grouped.set(key, list);
  });

  return rows.map((row) => ({
    ...row,
    ubicacion_detalles: compactLocation(row),
    sondeos: grouped.get(String(row.id || "")) || [],
  }));
}

async function laboratoryNames(rows: JsonRecord[]) {
  const ids = [...new Set(rows.map((row) => String(row.laboratorio_id || "")).filter(Boolean))];
  if (!ids.length) return new Map<string, string>();
  const { data, error } = await schema("calidad").from("laboratorios").select("id,entidad").in("id", ids);
  if (error) throw new Error(error.message);
  return new Map((data || []).map((item) => [String(item.id), String(item.entidad || "Laboratorio")]));
}

async function specificationNames(rows: JsonRecord[]) {
  const ids = [...new Set(rows.map((row) => String(row.especificacion_id || "")).filter(Boolean))];
  if (!ids.length) return new Map<string, string>();
  const { data, error } = await schema("calidad").from("v_especificaciones").select("id,codigo,titulo").in("id", ids);
  if (error) throw new Error(error.message);
  return new Map((data || []).map((item) => [String(item.id), [item.codigo, item.titulo].filter(Boolean).join(" — ")]));
}

function shapeGenericDetails(row: JsonRecord, detailEntries: Array<[string, unknown]>) {
  const shaped = {} as JsonRecord;
  Object.entries(row).forEach(([key, value]) => {
    Object.defineProperty(shaped, key, { value, enumerable: false, configurable: true, writable: true });
  });
  detailEntries.forEach(([key, value]) => {
    if (value == null || value === "") return;
    Object.defineProperty(shaped, key, { value, enumerable: true, configurable: true, writable: true });
  });
  return shaped;
}

async function enrichWelding(rows: JsonRecord[]) {
  const equipmentIds = [...new Set(rows.map((row) => String(row.equipo_id || "")).filter(Boolean))];
  let equipment = new Map<string, string>();
  if (equipmentIds.length) {
    const { data, error } = await schema("calidad")
      .from("inv_equip_lab")
      .select("id,codigo_interno,descripcion,marca,modelo,numero_serie")
      .in("id", equipmentIds);
    if (error) throw new Error(error.message);
    equipment = new Map((data || []).map((item) => {
      const label = [item.codigo_interno, item.descripcion, item.marca, item.modelo, item.numero_serie].filter(Boolean).join(" · ");
      return [String(item.id), label || "Equipo"];
    }));
  }
  const labs = await laboratoryNames(rows);
  return rows.map((row) => {
    const elemento = String(row.elemento || "").trim();
    const ubicacion = String(row.ubicacion || "").trim();
    const base = {
      ...row,
      ubicacion_resultado: elemento || ubicacion || null,
      equipo_nombre: equipment.get(String(row.equipo_id || "")) || null,
      inspector_nombre: row.nombre_completo || null,
      laboratorio_nombre: labs.get(String(row.laboratorio_id || "")) || null,
    } as JsonRecord;
    return shapeGenericDetails(base, [
      ["FECHA INFORME", row.fecha_informe],
      ["LABORATORIO", base.laboratorio_nombre],
      ["INSPECTOR", base.inspector_nombre],
      ["EQUIPO", base.equipo_nombre],
      ["COMENTARIOS", row.comentarios],
    ]);
  });
}

async function enrichBallastSpecifications(rows: JsonRecord[]) {
  const specs = await specificationNames(rows);
  const labs = await laboratoryNames(rows);
  return rows.map((row) => {
    const base = {
      ...row,
      especificacion: specs.get(String(row.especificacion_id || "")) || null,
      laboratorio_nombre: labs.get(String(row.laboratorio_id || "")) || null,
    } as JsonRecord;
    return shapeGenericDetails(base, [
      ["ESTUDIO", row.entregable],
      ["LABORATORIO", base.laboratorio_nombre],
      ["LUGAR MUESTREO", row.lugar_muestreo],
      ["FECHA INFORME", row.fecha_informe],
      ["ESPECIFICACIÓN", base.especificacion],
      ["LOTE", row.lote],
      ["DENSIDAD", row.densidad],
      ["% ABSORCIÓN", row.absorcion],
      ["% PARTÍCULAS DELEZNABLES", row.particulas_deleznables],
      ["% DESGASTE LA", row.desgaste],
      ["% INTEMPERISMO EN Na₂SO₄", row.intemperismo_sodio],
      ["% PARTÍCULAS PLANAS Y ALARGADAS", row.elementos_aciculares_lajosos],
      ["% ESPESORES ENTRE 25 Y 16 MM", row.em_25_16],
      ["% ESPESORES < 16 MM", row.em_16],
      ["% ELEMENTOS >= 100 MM", row.longitud_100],
      ["% PASA 0.075 MM", row.pasa_0075],
      ["% PASA 0.063 MM (VÍA HÚMEDA)", row.pasa_0063],
      ["COMENTARIOS", row.comentarios],
    ]);
  });
}

async function enrichCalidadesDetails(rows: JsonRecord[], resultType: string) {
  const specs = await specificationNames(rows);
  const labs = await laboratoryNames(rows);
  return rows.map((row) => {
    const base = {
      ...row,
      especificacion_nombre: specs.get(String(row.especificacion_id || "")) || null,
      laboratorio_nombre: labs.get(String(row.laboratorio_id || "")) || null,
    } as JsonRecord;

    const common: Array<[string, unknown]> = [
      ["FECHA INFORME", row.fecha_informe],
      ["LABORATORIO", base.laboratorio_nombre],
      ["LUGAR MUESTREO", row.lugar_muestreo],
      ["LOTE", row.lote],
      ["ESPECIFICACIÓN", base.especificacion_nombre],
      ["LÍMITE LÍQUIDO (%)", row.limite_liquido],
      ["LÍMITE PLÁSTICO (%)", row.limite_plastico],
      ["ÍNDICE PLÁSTICO (%)", row.indice_plastico ?? row.ip],
      ["EQUIVALENTE ARENA (%)", row.equivalente_arena],
      ["CBR (%)", row.cbr],
      ["DESGASTE LA (%)", row.dla],
    ];

    if (resultType === "petreos_filtro") {
      return shapeGenericDetails(base, [
        ...common,
        ["MVSM (KG/M3)", row.mvsm],
        ["HUMEDAD ÓPTIMA (%)", row.humedad],
        ["Dᶠ₁₅/Dˢ₁₅", row.df15_ds15],
        ["Dᶠ₁₅/Dˢ₈₅", row.df15_ds85],
        ["Dᶠ₅₀/Dˢ₅₀", row.df50_ds50],
        ["Dᶠ₈₅/d o Dᶠ/a", row.df85_d],
        ["Dᶠ₆₀/Dᶠ₁₀", row.df60_df10],
        ["SUCS", row.sucs],
        ["COMENTARIOS", row.comentarios],
      ]);
    }

    return shapeGenericDetails(base, [
      ...common,
      ["PARTÍCULAS ALARGADAS (%)", row.p_alarg],
      ["PARTÍCULAS LAJEADAS (%)", row.p_laj],
      ["MVSM (KG/M3)", row.mvsm],
      ["HUMEDAD ÓPTIMA (%)", row.humedad],
      ["SUCS", row.sucs],
      ["COMENTARIOS", row.comentarios],
    ]);
  });
}

export async function listQualityResults(resultType: string, context: WorkContext, filters: QualityResultFilters = {}): Promise<QualityResultList> {
  if (resultType === "acero_refuerzo") {
    const payload: JsonRecord = {};
    if (filters.start) payload.fecha_desde = filters.start;
    if (filters.end) payload.fecha_hasta = filters.end;
    if (filters.frontId) payload.frente_id = filters.frontId;
    if (filters.subfrontId) payload.subfrente_id = filters.subfrontId;
    return steelListResponse(await callSteelApi("listar", context, payload));
  }
  const response = await callResultsApi("listar", resultType, context, {
    desde: filters.start || null,
    hasta: filters.end || null,
    frente_id: filters.frontId || null,
    subfrente_id: filters.subfrontId || null,
    buscar: filters.search?.trim() || null,
    dictamen: filters.dictamen || null,
    pagina: filters.page ?? 0,
    limite: filters.pageSize ?? 100,
  });

  let rows = Array.isArray(response.rows) ? response.rows as JsonRecord[] : [];
  if (resultType === "compactacion_suelos") rows = await enrichCompactionSoundings(rows, context);
  if (resultType === "estructuras_soldadura") rows = await enrichWelding(rows);
  if (resultType === "petreos_balasto") rows = await enrichBallastSpecifications(rows);
  if (["petreos_pavimento", "petreos_filtro", "petreos_subbalasto"].includes(resultType)) rows = await enrichCalidadesDetails(rows, resultType);

  return {
    rows,
    count: Number(response.count || 0),
    dictamenes: Array.isArray(response.dictamenes) ? response.dictamenes.map(String) : [],
  };
}

export async function createQualityResult(resultType: string, context: WorkContext, values: JsonRecord) {
  const sanitized = { ...values };
  delete sanitized.dictamen;
  delete sanitized.capturista;
  delete sanitized.ultimo_editor;
  const { data, error } = await schema("calidad").rpc("api_insertar_resultado_calidad" as never, {
    p_tipo: resultType,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: sanitized,
  } as never);
  return unwrap(data, error);
}

export async function updateQualityResult(resultType: string, id: string, context: WorkContext, changes: JsonRecord) {
  const sanitized = { ...changes };
  delete sanitized.dictamen;
  delete sanitized.capturista;
  delete sanitized.ultimo_editor;
  return callResultsApi("actualizar", resultType, context, { id, changes: sanitized });
}

export async function deleteQualityResult(resultType: string, id: string, context: WorkContext) {
  return callResultsApi("eliminar", resultType, context, { id });
}
